import React, { useState, useEffect, useCallback } from 'react';
import PersonaTagChips, { PERSONA_VOCAB } from '../components/PersonaTagChips';

const CATEGORIES = ['Templates', 'Policies', 'Training', 'Other']; // mirrors Toolkit:AllowedCategories
const MAX_FILE_BYTES = 50 * 1024 * 1024; // 50 MB — mirrors Toolkit:MaxFileSizeBytes

const ALLOWED_MIME_ACCEPT = [
  '.pdf', '.docx', '.xlsx', '.pptx', '.txt', '.md', '.zip', '.png', '.jpg', '.jpeg',
].join(',');

const CATEGORY_COLORS = {
  Templates: '#1565C0',
  Policies:  '#6A1B9A',
  Training:  '#00796B',
  Other:     '#455A64',
};

function formatBytes(b) {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }); }
  catch { return iso; }
}

const initialForm = () => ({
  title: '', description: '', category: 'Templates',
  personaTags: ['assessor'],
  file: null,
});

export default function ToolkitAdmin({ API, persona }) {
  const [form, setForm]       = useState(initialForm());
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState(false);
  const [toast, setToast]     = useState(null);   // { kind: 'ok' | 'err', text }
  const [editing, setEditing] = useState(null);   // item being edited

  // Auth + active-tenant headers are attached by the global fetch interceptor
  // installed in src/auth/msalConfig.js — no per-call header mapping required.
  const headers = useCallback(() => ({}), []);

  const showToast = (kind, text) => {
    setToast({ kind, text });
    setTimeout(() => setToast(null), 4000);
  };

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/v1/toolkit?page=1&pageSize=200`, { headers: headers() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setItems(body.items || []);
    } catch (e) {
      setError(e.message || 'Failed to load toolkit');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [API, headers]);

  useEffect(() => { reload(); }, [reload]);

  const validateForm = () => {
    if (!form.title.trim() || form.title.trim().length < 3) return 'Title must be at least 3 characters.';
    if (form.description && form.description.length > 1000) return 'Description must be 1000 characters or fewer.';
    if (!CATEGORIES.includes(form.category)) return 'Pick a category.';
    if (form.personaTags.length === 0) return 'Pick at least one persona.';
    if (!form.file) return 'Choose a file.';
    if (form.file.size === 0) return 'File is empty.';
    if (form.file.size > MAX_FILE_BYTES) return `File must be ≤ ${formatBytes(MAX_FILE_BYTES)} (selected ${formatBytes(form.file.size)}).`;
    return null;
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const err = validateForm();
    if (err) { showToast('err', err); return; }

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('File', form.file);
      fd.append('Title', form.title.trim());
      if (form.description) fd.append('Description', form.description.trim());
      fd.append('Category', form.category);
      // Backend's ParsePersonaTags accepts comma-separated OR repeated form fields.
      form.personaTags.forEach(t => fd.append('PersonaTags', t));

      const res = await fetch(`${API}/api/v1/toolkit`, {
        method: 'POST',
        headers: headers(),
        body: fd,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Upload failed (HTTP ${res.status})`);
      }
      showToast('ok', `Uploaded "${form.title.trim()}".`);
      setForm(initialForm());
      // Reset the native file input
      const fileEl = document.getElementById('toolkit-file-input');
      if (fileEl) fileEl.value = '';
      await reload();
    } catch (e) {
      showToast('err', e.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Permanently delete "${item.title}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/api/v1/toolkit/${item.id}`, {
        method: 'DELETE',
        headers: headers(),
      });
      if (!res.ok) throw new Error(`Delete failed (HTTP ${res.status})`);
      showToast('ok', `Deleted "${item.title}".`);
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (e) {
      showToast('err', e.message || 'Delete failed');
      reload();
    }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    if (!editing.title.trim() || editing.title.trim().length < 3) {
      showToast('err', 'Title must be at least 3 characters.'); return;
    }
    if (!CATEGORIES.includes(editing.category)) { showToast('err', 'Pick a category.'); return; }
    if (!editing.personaTags || editing.personaTags.length === 0) {
      showToast('err', 'Pick at least one persona.'); return;
    }
    try {
      const res = await fetch(`${API}/api/v1/toolkit/${editing.id}`, {
        method: 'PUT',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title:       editing.title.trim(),
          description: editing.description ? editing.description.trim() : null,
          category:    editing.category,
          personaTags: editing.personaTags,
        }),
      });
      if (!res.ok) throw new Error(`Save failed (HTTP ${res.status})`);
      showToast('ok', `Saved "${editing.title.trim()}".`);
      setEditing(null);
      reload();
    } catch (e) {
      showToast('err', e.message || 'Save failed');
    }
  };

  return (
    <div>
      {toast && <Toast kind={toast.kind} text={toast.text} onClose={() => setToast(null)} />}

      {/* ── Upload form ── */}
      <div style={{background:'#fff',border:'1px solid #E8ECF0',borderRadius:10,padding:18,marginBottom:24}}>
        <div style={{fontSize:13,fontWeight:600,color:'#0D1B2A',marginBottom:14}}>Upload new toolkit item</div>

        <form onSubmit={handleUpload}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <Field label="Title" required>
              <input
                type="text" value={form.title} disabled={busy}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Acceptable Use Policy template"
                style={inputStyle}
              />
            </Field>
            <Field label="Category" required>
              <select value={form.category} disabled={busy}
                onChange={e => setForm({ ...form, category: e.target.value })}
                style={inputStyle}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          <div style={{marginTop:14}}>
            <Field label="Description">
              <textarea value={form.description || ''} disabled={busy}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="What is this item for? Who should use it?"
                style={{...inputStyle, minHeight: 70, resize: 'vertical'}}
              />
            </Field>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginTop:14}}>
            <Field label="Audience (persona tags)" required>
              <PersonaTagChips
                tags={form.personaTags}
                onChange={tags => setForm({ ...form, personaTags: tags })}
                editable
              />
              <div style={{fontSize:10,color:'#7A8899',marginTop:6}}>
                Click a chip to toggle. Selected tags are highlighted.
              </div>
            </Field>
            <Field label="File" required>
              <input
                id="toolkit-file-input"
                type="file" disabled={busy}
                accept={ALLOWED_MIME_ACCEPT}
                onChange={e => setForm({ ...form, file: e.target.files?.[0] || null })}
                style={{fontSize:12}}
              />
              <div style={{fontSize:10,color:'#7A8899',marginTop:6}}>
                Up to {formatBytes(MAX_FILE_BYTES)}. Allowed: PDF, DOCX, XLSX, PPTX, TXT, MD, ZIP, PNG, JPG.
              </div>
              {form.file && (
                <div style={{fontSize:11,color:'#1565C0',marginTop:6}}>
                  {form.file.name} · {formatBytes(form.file.size)}
                </div>
              )}
            </Field>
          </div>

          <div style={{marginTop:18,display:'flex',gap:10,justifyContent:'flex-end'}}>
            <button type="button" className="btn-secondary" disabled={busy}
              onClick={() => { setForm(initialForm()); const f = document.getElementById('toolkit-file-input'); if (f) f.value = ''; }}>
              Reset
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </form>
      </div>

      {/* ── Existing items ── */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
        <div style={{fontSize:13,fontWeight:600,color:'#0D1B2A'}}>Existing items <span style={{color:'#7A8899',fontWeight:400}}>({items.length})</span></div>
        <button className="btn-secondary" onClick={reload} disabled={loading}>↻ Refresh</button>
      </div>

      {loading && <div style={{padding:'40px 0',textAlign:'center',color:'#7A8899',fontSize:12}}>Loading…</div>}
      {error && (
        <div style={{padding:14,background:'#FEEBEE',border:'1px solid #FFCDD2',borderRadius:8,color:'#C62828',fontSize:12,marginBottom:14}}>
          {error}
        </div>
      )}
      {!loading && !error && items.length === 0 && (
        <div style={{padding:'40px 0',textAlign:'center',color:'#7A8899',fontSize:12}}>
          No items uploaded yet. Use the form above to add the first one.
        </div>
      )}
      {items.length > 0 && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:14}}>
          {items.map(item => (
            <AdminCard
              key={item.id}
              item={item}
              onEdit={() => setEditing({
                id: item.id, title: item.title,
                description: item.description || '',
                category: item.category,
                personaTags: [...(item.personaTags || [])],
              })}
              onDelete={() => handleDelete(item)}
            />
          ))}
        </div>
      )}

      {/* ── Edit modal ── */}
      {editing && (
        <Modal onClose={() => setEditing(null)}>
          <div style={{fontSize:14,fontWeight:600,color:'#0D1B2A',marginBottom:14}}>Edit toolkit item</div>
          <Field label="Title" required>
            <input type="text" value={editing.title}
              onChange={e => setEditing({ ...editing, title: e.target.value })} style={inputStyle}/>
          </Field>
          <div style={{marginTop:12}}>
            <Field label="Description">
              <textarea value={editing.description}
                onChange={e => setEditing({ ...editing, description: e.target.value })}
                style={{...inputStyle, minHeight: 70, resize: 'vertical'}}/>
            </Field>
          </div>
          <div style={{marginTop:12}}>
            <Field label="Category" required>
              <select value={editing.category}
                onChange={e => setEditing({ ...editing, category: e.target.value })} style={inputStyle}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <div style={{marginTop:12}}>
            <Field label="Audience (persona tags)" required>
              <PersonaTagChips
                tags={editing.personaTags}
                onChange={tags => setEditing({ ...editing, personaTags: tags })}
                editable
              />
            </Field>
          </div>
          <div style={{marginTop:18,display:'flex',gap:10,justifyContent:'flex-end'}}>
            <button className="btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn-primary" onClick={handleSaveEdit}>Save changes</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const inputStyle = {
  width:'100%', padding:'8px 10px', fontSize:12, border:'1px solid #DDE1E6',
  borderRadius:6, outline:'none', fontFamily:'inherit', background:'#fff',
};

function Field({ label, required, children }) {
  return (
    <div>
      <label style={{display:'block',fontSize:11,fontWeight:500,color:'#0D1B2A',marginBottom:6}}>
        {label}{required && <span style={{color:'#C62828',marginLeft:3}}>*</span>}
      </label>
      {children}
    </div>
  );
}

function AdminCard({ item, onEdit, onDelete }) {
  return (
    <div style={{background:'#fff',border:'1px solid #E8ECF0',borderRadius:10,padding:14,display:'flex',flexDirection:'column',gap:10}}>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8}}>
        <div style={{fontSize:13,fontWeight:600,color:'#0D1B2A',lineHeight:1.4,flex:1}}>{item.title}</div>
        <span style={{
          fontSize:9,padding:'2px 7px',borderRadius:6,whiteSpace:'nowrap',
          background:(CATEGORY_COLORS[item.category]||'#455A64')+'14',
          color: CATEGORY_COLORS[item.category]||'#455A64',
          fontWeight:500,
        }}>{item.category}</span>
      </div>
      {item.description && (
        <div style={{fontSize:11,color:'#7A8899',lineHeight:1.5,maxHeight:48,overflow:'hidden',textOverflow:'ellipsis'}}>
          {item.description}
        </div>
      )}
      <PersonaTagChips tags={item.personaTags} editable={false} size="sm" />
      <div style={{fontSize:10,color:'#7A8899',display:'flex',justifyContent:'space-between',marginTop:'auto',paddingTop:6,borderTop:'1px solid #F0F2F5'}}>
        <span>{formatBytes(item.sizeBytes)} · {formatDate(item.uploadedAt)}</span>
        <div style={{display:'flex',gap:6}}>
          <button className="btn-ghost" style={{fontSize:11}} onClick={onEdit}>Edit</button>
          <button className="btn-ghost" style={{fontSize:11,color:'#C62828'}} onClick={onDelete}>Delete</button>
        </div>
      </div>
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position:'fixed', inset:0, background:'rgba(13,27,42,0.45)',
        display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000,
      }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:'#fff', borderRadius:10, padding:22, width:'min(560px,90vw)',
        maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.25)',
      }}>
        {children}
      </div>
    </div>
  );
}

function Toast({ kind, text, onClose }) {
  const isOk = kind === 'ok';
  return (
    <div
      onClick={onClose}
      style={{
        position:'fixed', top:20, right:24, zIndex:1100,
        padding:'10px 14px', borderRadius:8, fontSize:12,
        background: isOk ? '#E8F5E9' : '#FEEBEE',
        color: isOk ? '#2E7D32' : '#C62828',
        border: `1px solid ${isOk ? '#A5D6A7' : '#FFCDD2'}`,
        cursor:'pointer', maxWidth:360,
      }}>
      {text}
    </div>
  );
}
